/**
 * Category Selector Component
 *
 * Hierarchical category selection with ability to create new categories.
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';

interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  description?: string;
  children?: Category[];
}

interface CategorySelectorProps {
  storeId: string;
  selectedCategories: string[];
  onChange: (categoryIds: string[]) => void;
}

export default function CategorySelector({
  storeId,
  selectedCategories,
  onChange,
}: CategorySelectorProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [flatCategories, setFlatCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const loadCategories = useCallback(async () => {
    if (!storeId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/stores/${storeId}/categories`);
      const data = await response.json() as {
        categories: Category[];
        flatCategories: Category[];
      };
      setCategories(data.categories || []);
      setFlatCategories(data.flatCategories || []);

      // Expand categories that have selected children
      const toExpand = new Set<string>();
      for (const catId of selectedCategories) {
        const cat = (data.flatCategories || []).find(c => c.id === catId);
        if (cat?.parent_id) {
          toExpand.add(cat.parent_id);
          // Also expand grandparents
          const parent = (data.flatCategories || []).find(c => c.id === cat.parent_id);
          if (parent?.parent_id) {
            toExpand.add(parent.parent_id);
          }
        }
      }
      setExpandedCategories(toExpand);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [storeId, selectedCategories]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      setCreating(true);
      await api.post(`/api/ecommerce/stores/${storeId}/categories`, {
        name: newCategoryName.trim(),
        parent_id: newCategoryParent,
      });
      setShowCreateModal(false);
      setNewCategoryName('');
      setNewCategoryParent(null);
      await loadCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    if (selectedCategories.includes(categoryId)) {
      onChange(selectedCategories.filter(id => id !== categoryId));
    } else {
      onChange([...selectedCategories, categoryId]);
    }
  };

  const toggleExpanded = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategoryItem = (category: Category, depth: number = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const isSelected = selectedCategories.includes(category.id);

    return (
      <div key={category.id}>
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-colors cursor-pointer ${
            isSelected
              ? 'bg-teal-50 dark:bg-teal-900/30'
              : 'hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
        >
          {/* Expand/Collapse Button */}
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(category.id);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            >
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <span className="w-5" />
          )}

          {/* Checkbox */}
          <button
            type="button"
            onClick={() => toggleCategory(category.id)}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? 'bg-teal-600 border-teal-600'
                : 'border-gray-300 dark:border-gray-600 hover:border-teal-500'
            }`}
          >
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {/* Category Name */}
          <span
            className={`flex-1 text-sm ${
              isSelected
                ? 'font-medium text-teal-700 dark:text-teal-400'
                : 'text-gray-700 dark:text-gray-300'
            }`}
            onClick={() => toggleCategory(category.id)}
          >
            {category.name}
          </span>

          {/* Add Subcategory Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setNewCategoryParent(category.id);
              setShowCreateModal(true);
            }}
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-opacity"
            title="Add subcategory"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="group">
            {category.children!.map(child => renderCategoryItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Category List */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl max-h-64 overflow-y-auto">
        {categories.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            <p className="text-sm">No categories yet</p>
          </div>
        ) : (
          <div className="p-2">
            {categories.map(cat => renderCategoryItem(cat))}
          </div>
        )}
      </div>

      {/* Add Category Button */}
      <button
        type="button"
        onClick={() => {
          setNewCategoryParent(null);
          setShowCreateModal(true);
        }}
        className="w-full py-2 text-sm font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 border border-dashed border-teal-300 dark:border-teal-700 rounded-lg transition-colors"
      >
        + Add Category
      </button>

      {/* Selected Categories Display */}
      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedCategories.map(catId => {
            const cat = flatCategories.find(c => c.id === catId);
            if (!cat) return null;
            return (
              <span
                key={catId}
                className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 text-sm rounded-full"
              >
                {cat.name}
                <button
                  type="button"
                  onClick={() => toggleCategory(catId)}
                  className="p-0.5 hover:bg-teal-200 dark:hover:bg-teal-800 rounded-full"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Create Category Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {newCategoryParent ? 'Add Subcategory' : 'Add Category'}
              </h3>
              {newCategoryParent && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Under: {flatCategories.find(c => c.id === newCategoryParent)?.name}
                </p>
              )}
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Electronics"
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateCategory();
                  }}
                />
              </div>

              {!newCategoryParent && flatCategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Parent Category (optional)
                  </label>
                  <select
                    value={newCategoryParent || ''}
                    onChange={(e) => setNewCategoryParent(e.target.value || null)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">None (top level)</option>
                    {flatCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {'—'.repeat(getDepth(cat, flatCategories))} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCategoryName('');
                  setNewCategoryParent(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim() || creating}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to calculate category depth
function getDepth(category: Category, allCategories: Category[]): number {
  let depth = 0;
  let currentId = category.parent_id;
  while (currentId) {
    depth++;
    const parent = allCategories.find(c => c.id === currentId);
    currentId = parent?.parent_id || null;
  }
  return depth;
}
